/**
 * QuoteVersionDiff — Side-by-side comparison of two quotation versions.
 * SALES-P2-3: QuoteVersion Diff UI
 *
 * Highlights:
 *  - Changed fields: amber background
 *  - Added items: green background
 *  - Removed items: red background
 */

import { getQuotationById, type Quotation, type QuotationItem } from '@/api/quotations';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui';
import { useTranslation } from '@kgc/i18n';
import { Badge } from '@kgc/ui';
import { useCallback, useEffect, useState } from 'react';

// ============================================
// TYPES
// ============================================

interface QuoteVersionDiffProps {
  quoteIdA: string;
  quoteIdB: string;
  onClose?: () => void;
}

type DiffStatus = 'unchanged' | 'changed' | 'added' | 'removed';

interface FieldDiff {
  field: string;
  labelKey: string;
  valueA: string;
  valueB: string;
  status: DiffStatus;
}

interface ItemDiff {
  description: string;
  status: DiffStatus;
  itemA?: QuotationItem;
  itemB?: QuotationItem;
}

// ============================================
// HELPERS
// ============================================

function formatPrice(price: number): string {
  return new Intl.NumberFormat('hu-HU', {
    style: 'currency',
    currency: 'HUF',
    maximumFractionDigits: 0,
  }).format(price);
}

function formatDate(dateStr: string | undefined): string {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('hu-HU');
}

function compareFields(a: Quotation, b: Quotation): FieldDiff[] {
  const fields: { key: keyof Quotation; labelKey: string; format?: (v: unknown) => string }[] = [
    {
      key: 'totalGross',
      labelKey: 'quotation.grossTotal',
      format: v => formatPrice(Number(v ?? 0)),
    },
    {
      key: 'subtotalNet',
      labelKey: 'quotation.netTotal',
      format: v => formatPrice(Number(v ?? 0)),
    },
    { key: 'vatAmount', labelKey: 'quotation.vatTotal', format: v => formatPrice(Number(v ?? 0)) },
    {
      key: 'validUntil',
      labelKey: 'quotation.validUntilLabel',
      format: v => formatDate(String(v ?? '')),
    },
    { key: 'status', labelKey: 'quotation.statusLabel' },
    { key: 'notes', labelKey: 'quotation.customerNote' },
    { key: 'partnerName', labelKey: 'quotation.partnerName' },
  ];

  return fields.map(({ key, labelKey, format }) => {
    const rawA = a[key];
    const rawB = b[key];
    const valA = format ? format(rawA) : String(rawA ?? '-');
    const valB = format ? format(rawB) : String(rawB ?? '-');
    return {
      field: key,
      labelKey,
      valueA: valA,
      valueB: valB,
      status: valA === valB ? 'unchanged' : 'changed',
    };
  });
}

function compareItems(a: Quotation, b: Quotation): ItemDiff[] {
  const result: ItemDiff[] = [];
  const matchedB = new Set<number>();

  for (const itemA of a.items) {
    // Try to match by productId first, then by description
    const bIdx = b.items.findIndex(
      (itemB, idx) =>
        !matchedB.has(idx) &&
        ((itemA.productId && itemA.productId === itemB.productId) ||
          itemA.description === itemB.description)
    );

    if (bIdx >= 0) {
      matchedB.add(bIdx);
      const itemB = b.items[bIdx]!;
      const hasChanges =
        itemA.quantity !== itemB.quantity ||
        itemA.unitPriceNet !== itemB.unitPriceNet ||
        itemA.vatRate !== itemB.vatRate ||
        itemA.totalGross !== itemB.totalGross;

      result.push({
        description: itemA.description,
        status: hasChanges ? 'changed' : 'unchanged',
        itemA,
        itemB,
      });
    } else {
      result.push({
        description: itemA.description,
        status: 'removed',
        itemA,
      });
    }
  }

  // Items in B that were not matched = added
  b.items.forEach((itemB, idx) => {
    if (!matchedB.has(idx)) {
      result.push({
        description: itemB.description,
        status: 'added',
        itemB,
      });
    }
  });

  return result;
}

// ============================================
// COMPONENT
// ============================================

export function QuoteVersionDiff({ quoteIdA, quoteIdB, onClose }: QuoteVersionDiffProps) {
  const { t } = useTranslation();
  const [quoteA, setQuoteA] = useState<Quotation | null>(null);
  const [quoteB, setQuoteB] = useState<Quotation | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchBoth = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [resA, resB] = await Promise.all([
        getQuotationById(quoteIdA),
        getQuotationById(quoteIdB),
      ]);
      setQuoteA(resA.data);
      setQuoteB(resB.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('error.common.operationFailed'));
    } finally {
      setIsLoading(false);
    }
  }, [quoteIdA, quoteIdB, t]);

  useEffect(() => {
    void fetchBoth();
  }, [fetchBoth]);

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <p className="text-muted-foreground">{t('common.loading')}</p>
        </CardContent>
      </Card>
    );
  }

  if (error || !quoteA || !quoteB) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <p className="text-red-600 dark:text-red-400">
            {error ?? t('error.common.operationFailed')}
          </p>
        </CardContent>
      </Card>
    );
  }

  const fieldDiffs = compareFields(quoteA, quoteB);
  const itemDiffs = compareItems(quoteA, quoteB);
  const hasAnyChange =
    fieldDiffs.some(f => f.status !== 'unchanged') || itemDiffs.some(i => i.status !== 'unchanged');

  const statusBg = (status: DiffStatus) => {
    switch (status) {
      case 'changed':
        return 'bg-amber-50 dark:bg-amber-900/20';
      case 'added':
        return 'bg-green-50 dark:bg-green-900/20';
      case 'removed':
        return 'bg-red-50 dark:bg-red-900/20';
      default:
        return '';
    }
  };

  const statusBadge = (status: DiffStatus) => {
    switch (status) {
      case 'changed':
        return (
          <Badge
            variant="outline"
            className="border-amber-300 text-amber-700 dark:border-amber-600 dark:text-amber-300"
          >
            {t('quotation.diff.changed')}
          </Badge>
        );
      case 'added':
        return (
          <Badge
            variant="outline"
            className="border-green-300 text-green-700 dark:border-green-600 dark:text-green-300"
          >
            {t('quotation.diff.added')}
          </Badge>
        );
      case 'removed':
        return (
          <Badge
            variant="outline"
            className="border-red-300 text-red-700 dark:border-red-600 dark:text-red-300"
          >
            {t('quotation.diff.removed')}
          </Badge>
        );
      default:
        return null;
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>{t('quotation.diff.compareVersions')}</CardTitle>
          {onClose && (
            <button
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground"
              aria-label={t('common.close')}
            >
              &times;
            </button>
          )}
        </div>
        <p className="text-sm text-muted-foreground">
          {t('quotation.diff.versionN', { n: String(quoteA.version ?? 1) })}
          {' → '}
          {t('quotation.diff.versionN', { n: String(quoteB.version ?? 1) })}
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        {!hasAnyChange && (
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-center dark:border-blue-800 dark:bg-blue-900/20">
            <p className="text-sm text-blue-700 dark:text-blue-300">
              {t('quotation.diff.noChanges')}
            </p>
          </div>
        )}

        {/* Field comparison */}
        <div>
          <h3 className="mb-3 text-sm font-semibold text-foreground">
            {t('quotation.diff.basicInfo')}
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b bg-gray-50 dark:bg-slate-700/50">
                <tr>
                  <th className="px-3 py-2 text-left text-muted-foreground">
                    {t('quotation.diff.field')}
                  </th>
                  <th className="px-3 py-2 text-left text-muted-foreground">
                    {t('quotation.diff.versionN', { n: String(quoteA.version ?? 1) })}
                  </th>
                  <th className="px-3 py-2 text-left text-muted-foreground">
                    {t('quotation.diff.versionN', { n: String(quoteB.version ?? 1) })}
                  </th>
                  <th className="px-3 py-2 text-center text-muted-foreground">
                    {t('table.status')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y dark:divide-slate-700">
                {fieldDiffs.map(diff => (
                  <tr key={diff.field} className={statusBg(diff.status)}>
                    <td className="px-3 py-2 font-medium text-foreground">{t(diff.labelKey)}</td>
                    <td className="px-3 py-2 text-foreground">{diff.valueA}</td>
                    <td className="px-3 py-2 text-foreground">
                      {diff.status === 'changed' ? (
                        <span className="font-semibold">{diff.valueB}</span>
                      ) : (
                        diff.valueB
                      )}
                    </td>
                    <td className="px-3 py-2 text-center">{statusBadge(diff.status)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Line items comparison */}
        <div>
          <h3 className="mb-3 text-sm font-semibold text-foreground">{t('invoice.items')}</h3>

          {/* Desktop: side-by-side */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b bg-gray-50 dark:bg-slate-700/50">
                <tr>
                  <th className="px-3 py-2 text-left text-muted-foreground">
                    {t('quotation.itemDescription')}
                  </th>
                  <th className="px-3 py-2 text-right text-muted-foreground">
                    {t('common.quantity')} (
                    {t('quotation.diff.versionN', { n: String(quoteA.version ?? 1) })})
                  </th>
                  <th className="px-3 py-2 text-right text-muted-foreground">
                    {t('common.quantity')} (
                    {t('quotation.diff.versionN', { n: String(quoteB.version ?? 1) })})
                  </th>
                  <th className="px-3 py-2 text-right text-muted-foreground">
                    {t('quotation.unitPrice')} (
                    {t('quotation.diff.versionN', { n: String(quoteA.version ?? 1) })})
                  </th>
                  <th className="px-3 py-2 text-right text-muted-foreground">
                    {t('quotation.unitPrice')} (
                    {t('quotation.diff.versionN', { n: String(quoteB.version ?? 1) })})
                  </th>
                  <th className="px-3 py-2 text-right text-muted-foreground">
                    {t('quotation.vat')}
                  </th>
                  <th className="px-3 py-2 text-center text-muted-foreground">
                    {t('table.status')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y dark:divide-slate-700">
                {itemDiffs.map((diff, idx) => (
                  <tr key={`${diff.description}-${idx}`} className={statusBg(diff.status)}>
                    <td className="px-3 py-2 font-medium text-foreground">{diff.description}</td>
                    <td className="px-3 py-2 text-right text-foreground">
                      {diff.itemA ? `${diff.itemA.quantity} ${diff.itemA.unit}` : '-'}
                    </td>
                    <td className="px-3 py-2 text-right text-foreground">
                      {diff.itemB ? (
                        diff.status === 'changed' &&
                        diff.itemA &&
                        diff.itemA.quantity !== diff.itemB.quantity ? (
                          <span className="font-semibold">
                            {diff.itemB.quantity} {diff.itemB.unit}
                          </span>
                        ) : (
                          `${diff.itemB.quantity} ${diff.itemB.unit}`
                        )
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="px-3 py-2 text-right text-foreground">
                      {diff.itemA ? formatPrice(diff.itemA.unitPriceNet) : '-'}
                    </td>
                    <td className="px-3 py-2 text-right text-foreground">
                      {diff.itemB ? (
                        diff.status === 'changed' &&
                        diff.itemA &&
                        diff.itemA.unitPriceNet !== diff.itemB.unitPriceNet ? (
                          <span className="font-semibold">
                            {formatPrice(diff.itemB.unitPriceNet)}
                          </span>
                        ) : (
                          formatPrice(diff.itemB.unitPriceNet)
                        )
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="px-3 py-2 text-right text-foreground">
                      {(diff.itemA ?? diff.itemB)?.vatRate ?? '-'}%
                    </td>
                    <td className="px-3 py-2 text-center">{statusBadge(diff.status)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile: stacked cards */}
          <div className="md:hidden space-y-3">
            {itemDiffs.map((diff, idx) => (
              <div
                key={`${diff.description}-mobile-${idx}`}
                className={`rounded-lg border p-3 ${statusBg(diff.status)} dark:border-slate-700`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-foreground text-sm">{diff.description}</span>
                  {statusBadge(diff.status)}
                </div>
                {diff.status !== 'added' && diff.itemA && (
                  <div className="text-xs text-muted-foreground">
                    {t('quotation.diff.versionN', { n: String(quoteA.version ?? 1) })}:{' '}
                    {diff.itemA.quantity} {diff.itemA.unit} &times;{' '}
                    {formatPrice(diff.itemA.unitPriceNet)} = {formatPrice(diff.itemA.totalGross)}
                  </div>
                )}
                {diff.status !== 'removed' && diff.itemB && (
                  <div className="text-xs text-foreground font-medium">
                    {t('quotation.diff.versionN', { n: String(quoteB.version ?? 1) })}:{' '}
                    {diff.itemB.quantity} {diff.itemB.unit} &times;{' '}
                    {formatPrice(diff.itemB.unitPriceNet)} = {formatPrice(diff.itemB.totalGross)}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Totals comparison */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="rounded-lg border p-4 dark:border-slate-700">
            <h4 className="text-sm font-medium text-muted-foreground mb-2">
              {t('quotation.diff.versionN', { n: String(quoteA.version ?? 1) })}
            </h4>
            <p className="text-2xl font-bold text-foreground">{formatPrice(quoteA.totalGross)}</p>
            <p className="text-xs text-muted-foreground">
              {t('quotation.netLabel')} {formatPrice(quoteA.subtotalNet)}
            </p>
          </div>
          <div
            className={`rounded-lg border p-4 dark:border-slate-700 ${quoteA.totalGross !== quoteB.totalGross ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-700' : ''}`}
          >
            <h4 className="text-sm font-medium text-muted-foreground mb-2">
              {t('quotation.diff.versionN', { n: String(quoteB.version ?? 1) })}
            </h4>
            <p className="text-2xl font-bold text-foreground">{formatPrice(quoteB.totalGross)}</p>
            <p className="text-xs text-muted-foreground">
              {t('quotation.netLabel')} {formatPrice(quoteB.subtotalNet)}
            </p>
            {quoteA.totalGross !== quoteB.totalGross && (
              <p
                className={`mt-1 text-xs font-medium ${quoteB.totalGross > quoteA.totalGross ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}
              >
                {quoteB.totalGross > quoteA.totalGross ? '+' : ''}
                {formatPrice(quoteB.totalGross - quoteA.totalGross)}
                {' ('}
                {quoteA.totalGross > 0
                  ? `${quoteB.totalGross > quoteA.totalGross ? '+' : ''}${(((quoteB.totalGross - quoteA.totalGross) / quoteA.totalGross) * 100).toFixed(1)}%`
                  : '-'}
                {')'}
              </p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
